// Features: M004-F0795 (WebUI #263, slice B1b: the groups editor inside the billing-profile drawer)
// Tests: T-M004-0414 (group validation, 12-group limit, invoice email), T-M004-0415 (bill-to: customer or "Someone else" inline;
//        an existing contact bill-to is shown read-only and kept unchanged)
//
// Rows are a flat form model (one object per group, with a stable uid). groupsForBody turns them into the contract's groups[] for the
// set body. Validation is the model's validateProfileDraft, run by the drawer on groupsForBody's output; this component only places each
// message on its field. The editor makes no contact reads or writes: the Ledger's contact frames key on a Core user id and cannot name a
// Ledger-only payer, so "Someone else" is an inline bill-to stored on the group (Ledger decision 2026-10-20).
import React from 'react';
import { timeWindowText, ADDRESS_LINE_MAX, ADDRESS_MAX_LINES } from './billingProfileModel';

export const MAX_GROUPS = 12;
export const AT_LIMIT_SENTENCE = 'A billing profile has at most 12 groups. Remove one to add another.';
const WEEKDAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const NATURES = [['class', 'Class'], ['rehearsal', 'Rehearsal'], ['other', 'Other']];

let uidSeq = 0;
const newUid = () => `grp-${++uidSeq}`;

// A group_key derived from a label: lower-case words joined by "_", at most 40 characters. The key stays editable.
export const slugify = (label) => String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

// A saved group as a row. Match keys this editor does not show (for example booking_id) ride along unchanged, so an edit never drops them.
// A saved contact bill-to keeps its contact_user_id (and display_name, when the Ledger sends one) for display; it is written back as-is.
export function rowFromGroup(group) {
  const { description_contains: descriptionContains, weekday, start_time_from: startFrom, start_time_to: startTo, room_id: roomId,
    nature_of_use: nature, date_from: dateFrom, date_to: dateTo, ...otherMatch } = group?.match || {};
  const billTo = group?.bill_to || {};
  const kind = billTo.kind === 'contact' || billTo.kind === 'inline' ? billTo.kind : 'customer';
  return {
    uid: newUid(),
    saved: true,
    keyTouched: true,
    label: group?.label ?? '',
    group_key: group?.group_key ?? '',
    billKind: kind,
    email: kind === 'inline' ? '' : (billTo.email ?? ''),
    contactUserId: kind === 'contact' && billTo.contact_user_id != null ? String(billTo.contact_user_id) : '',
    contactName: kind === 'contact' ? (billTo.display_name ?? '') : '',
    inlineName: kind === 'inline' ? (billTo.display_name ?? '') : '',
    inlineEmail: kind === 'inline' ? (billTo.email ?? '') : '',
    inlineAddress: kind === 'inline' && Array.isArray(billTo.address_lines) ? [...billTo.address_lines] : [],
    descriptionContains: descriptionContains ?? '',
    weekday: Array.isArray(weekday) ? weekday : (weekday ? [weekday] : []),
    startFrom: startFrom ?? '',
    startTo: startTo ?? '',
    roomId: roomId ?? '',
    nature: nature ?? '',
    dateFrom: dateFrom ?? '',
    dateTo: dateTo ?? '',
    otherMatch,
  };
}

export const rowsFromGroups = (groups) => (Array.isArray(groups) ? groups.map(rowFromGroup) : []);

export function blankRow() {
  return {
    uid: newUid(), saved: false, keyTouched: false, label: '', group_key: '', billKind: 'customer', email: '', contactUserId: '',
    contactName: '', inlineName: '', inlineEmail: '', inlineAddress: [], descriptionContains: '', weekday: [], startFrom: '', startTo: '',
    roomId: '', nature: '', dateFrom: '', dateTo: '', otherMatch: {},
  };
}

// The bill-to member of one group. "Customer (default)" sends { kind: 'customer' }. "Someone else" sends { kind: 'inline' } with a trimmed
// name and email and the non-blank address lines (omitted when there are none). A saved contact is written back as { kind, contact_user_id }
// and never changes unless the user picks another bill-to.
const billToFor = (r) => {
  if (r.billKind === 'inline') {
    const lines = r.inlineAddress.filter((line) => line.trim() !== '');
    return { kind: 'inline', display_name: r.inlineName.trim(), email: r.inlineEmail.trim(), ...(lines.length ? { address_lines: lines } : {}) };
  }
  const billTo = r.billKind === 'contact' ? { kind: 'contact', contact_user_id: Number(r.contactUserId) } : { kind: 'customer' };
  if (r.email.trim() !== '') billTo.email = r.email.trim();
  return billTo;
};

// The contract's groups[] from the rows. Empty optional members are omitted. Validation of the values is the model's (validateProfileDraft).
export function groupsForBody(rows) {
  return rows.map((r) => {
    const match = { ...r.otherMatch };
    if (r.descriptionContains !== '') match.description_contains = r.descriptionContains;
    const days = WEEKDAYS.map(([value]) => value).filter((value) => r.weekday.includes(value));
    if (days.length) match.weekday = days;
    if (r.startFrom !== '') match.start_time_from = r.startFrom;
    if (r.startTo !== '') match.start_time_to = r.startTo;
    if (r.roomId !== '') match.room_id = r.roomId;
    if (r.nature !== '') match.nature_of_use = r.nature;
    if (r.dateFrom !== '') match.date_from = r.dateFrom;
    if (r.dateTo !== '') match.date_to = r.dateTo;
    return { label: r.label, group_key: r.group_key, bill_to: billToFor(r), match };
  });
}

const Err = ({ id, text }) => (text ? <span id={id} role="alert" className="bps-field-error">{text}</span> : null);

function GroupRow({ row, index, count, messageAt, onPatch, onMove, onRemove }) {
  const base = `groups[${index}]`;
  const did = (name) => `${row.uid}-${name}`;
  const pathErr = (name, path) => ({ 'aria-invalid': messageAt(path) ? true : undefined, 'aria-describedby': messageAt(path) ? did(name) : undefined });
  const lines = row.inlineAddress;
  const setLine = (j, value) => onPatch({ inlineAddress: lines.map((line, k) => (k === j ? value : line)) });
  const removeLine = (j) => onPatch({ inlineAddress: lines.filter((_, k) => k !== j) });
  const addLine = () => onPatch({ inlineAddress: [...lines, ''] });

  const toggleDay = (value) => onPatch({ weekday: row.weekday.includes(value) ? row.weekday.filter((d) => d !== value) : [...row.weekday, value] });

  return (
    <fieldset className="bps-group" data-testid={`bp-group-${index}`}>
      <legend>Group {index + 1}{row.label ? `: ${row.label}` : ''}</legend>
      <label>Label
        <input type="text" value={row.label} {...pathErr('label', `${base}.label`)}
          onChange={(e) => onPatch({ label: e.target.value, ...(row.keyTouched ? {} : { group_key: slugify(e.target.value) }) })} />
      </label>
      <Err id={did('label')} text={messageAt(`${base}.label`)} />
      <label>Group key
        <input type="text" value={row.group_key} readOnly={row.saved} {...pathErr('key', `${base}.group_key`)}
          onChange={(e) => onPatch({ group_key: e.target.value, keyTouched: true })} />
      </label>
      <Err id={did('key')} text={messageAt(`${base}.group_key`)} />

      <fieldset className="bps-match">
        <legend>A booking is in this group when ALL of these hold</legend>
        <Err id={did('match')} text={messageAt(`${base}.match`)} />
        <label>Description contains
          <input type="text" value={row.descriptionContains} {...pathErr('desc', `${base}.match.description_contains`)}
            onChange={(e) => onPatch({ descriptionContains: e.target.value })} />
        </label>
        <Err id={did('desc')} text={messageAt(`${base}.match.description_contains`)} />
        <fieldset>
          <legend>Weekday</legend>
          {WEEKDAYS.map(([value, text]) => (
            <label key={value}><input type="checkbox" value={value} checked={row.weekday.includes(value)} onChange={() => toggleDay(value)} /> {text}</label>
          ))}
        </fieldset>
        <Err id={did('weekday')} text={messageAt(`${base}.match.weekday`)} />
        <label>Start time from
          <input type="time" value={row.startFrom} {...pathErr('from', `${base}.match.start_time_from`)}
            onChange={(e) => onPatch({ startFrom: e.target.value })} />
        </label>
        <label>Start time before
          <input type="time" value={row.startTo} {...pathErr('to', `${base}.match.start_time_to`)}
            onChange={(e) => onPatch({ startTo: e.target.value })} />
        </label>
        <Err id={did('from')} text={messageAt(`${base}.match.start_time_from`)} />
        <Err id={did('to')} text={messageAt(`${base}.match.start_time_to`)} />
        <p className="bps-window"><span>Window: </span><span data-testid={`bp-window-${index}`}>{timeWindowText(row.startFrom, row.startTo)}</span></p>
        <label>Room ID
          <input type="text" value={row.roomId} {...pathErr('room', `${base}.match.room_id`)} onChange={(e) => onPatch({ roomId: e.target.value })} />
        </label>
        <Err id={did('room')} text={messageAt(`${base}.match.room_id`)} />
        <label>Nature of use
          <select value={row.nature} {...pathErr('nature', `${base}.match.nature_of_use`)} onChange={(e) => onPatch({ nature: e.target.value })}>
            <option value="">Any</option>
            {NATURES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </label>
        <Err id={did('nature')} text={messageAt(`${base}.match.nature_of_use`)} />
        <label>Service date from
          <input type="date" value={row.dateFrom} {...pathErr('dfrom', `${base}.match.date_from`)} onChange={(e) => onPatch({ dateFrom: e.target.value })} />
        </label>
        <Err id={did('dfrom')} text={messageAt(`${base}.match.date_from`)} />
        <label>Service date to
          <input type="date" value={row.dateTo} {...pathErr('dto', `${base}.match.date_to`)} onChange={(e) => onPatch({ dateTo: e.target.value })} />
        </label>
        <Err id={did('dto')} text={messageAt(`${base}.match.date_to`)} />
      </fieldset>

      <fieldset className="bps-billto">
        <legend>Bill to</legend>
        {row.billKind === 'contact' ? (
          <p data-testid={`bp-contact-${index}`}>
            Bill to: {row.contactName || `contact for user ${row.contactUserId}`}. An existing contact, kept unless you choose another bill-to.
          </p>
        ) : null}
        <Err id={did('cid')} text={messageAt(`${base}.bill_to.contact_user_id`)} />
        <label><input type="radio" name={`${row.uid}-billto`} checked={row.billKind === 'customer'}
          onChange={() => onPatch({ billKind: 'customer' })} /> Customer (default)</label>
        <label><input type="radio" name={`${row.uid}-billto`} checked={row.billKind === 'inline'}
          onChange={() => onPatch({ billKind: 'inline' })} /> Someone else</label>
        <Err id={did('kind')} text={messageAt(`${base}.bill_to.kind`)} />

        {row.billKind === 'inline' ? (
          <div>
            <label>Name
              <input type="text" value={row.inlineName} {...pathErr('bname', `${base}.bill_to.display_name`)}
                onChange={(e) => onPatch({ inlineName: e.target.value })} />
            </label>
            <Err id={did('bname')} text={messageAt(`${base}.bill_to.display_name`)} />
            <label>Email (required, for sending the invoice)
              <input type="email" value={row.inlineEmail} {...pathErr('email', `${base}.bill_to.email`)}
                onChange={(e) => onPatch({ inlineEmail: e.target.value })} />
            </label>
            <fieldset className="bps-addresses">
              <legend>Address lines (optional, up to {ADDRESS_MAX_LINES})</legend>
              {lines.map((line, j) => (
                <div key={j}>
                  <label>Address line {j + 1}
                    <input type="text" maxLength={ADDRESS_LINE_MAX} value={line} {...pathErr(`addr${j}`, `${base}.bill_to.address_lines[${j}]`)}
                      onChange={(e) => setLine(j, e.target.value)} />
                  </label>
                  <button type="button" className="bps-secondary" onClick={() => removeLine(j)}>Remove address line {j + 1}</button>
                  <Err id={did(`addr${j}`)} text={messageAt(`${base}.bill_to.address_lines[${j}]`)} />
                </div>
              ))}
              <Err id={did('addr')} text={messageAt(`${base}.bill_to.address_lines`)} />
              <button type="button" className="bps-secondary" disabled={lines.length >= ADDRESS_MAX_LINES} onClick={addLine}>Add address line</button>
            </fieldset>
          </div>
        ) : (
          <label>Invoice email (optional, operator only)
            <input type="email" value={row.email} {...pathErr('email', `${base}.bill_to.email`)} onChange={(e) => onPatch({ email: e.target.value })} />
          </label>
        )}
        <Err id={did('email')} text={messageAt(`${base}.bill_to.email`)} />
      </fieldset>

      <div className="bps-group-actions">
        <button type="button" className="bps-secondary" onClick={() => onMove(-1)} disabled={index === 0}>Move group {index + 1} up</button>
        <button type="button" className="bps-secondary" onClick={() => onMove(1)} disabled={index === count - 1}>Move group {index + 1} down</button>
        <button type="button" className="bps-secondary" onClick={onRemove}>Remove group {index + 1}</button>
      </div>
    </fieldset>
  );
}

// rows: the draft (from rowsFromGroups or edits). onChange takes the next rows or an updater (a React setState works as is; the updater
// form keeps a late edit from overwriting newer ones). errors: validateProfileDraft's list for the whole draft. serverErrors: { path: sentence }.
export default function GroupsEditor({ rows, onChange, errors = [], serverErrors = {} }) {
  const messageAt = (path) => errors.find((e) => e.path === path)?.message || serverErrors[path] || '';
  const outsideGroups = errors.filter((e) => !e.path.startsWith('groups'));
  const update = (uid, patch) => onChange((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const move = (index, delta) => onChange((prev) => {
    const next = [...prev];
    const [moved] = next.splice(index, 1);
    next.splice(index + delta, 0, moved);
    return next;
  });
  const atLimit = rows.length >= MAX_GROUPS;

  return (
    <section className="bps-section" aria-labelledby="bp-groups-heading" data-testid="billing-groups-editor">
      <h4 id="bp-groups-heading">Groups</h4>
      {rows.map((row, index) => (
        <GroupRow
          key={row.uid}
          row={row}
          index={index}
          count={rows.length}
          messageAt={messageAt}
          onPatch={(patch) => update(row.uid, patch)}
          onMove={(delta) => move(index, delta)}
          onRemove={() => onChange((prev) => prev.filter((r) => r.uid !== row.uid))}
        />
      ))}
      <Err id="bp-groups-limit" text={messageAt('groups')} />
      {atLimit ? <p data-testid="billing-groups-limit">{AT_LIMIT_SENTENCE}</p> : null}
      <button type="button" className="bps-secondary" disabled={atLimit}
        onClick={() => onChange((prev) => [...prev, blankRow()])}>Add group</button>
      {outsideGroups.map((e) => <p key={e.path} role="alert" className="bps-field-error">{e.message}</p>)}
    </section>
  );
}

